/* TierAllowanceLedger — iter92 add-on.
 *
 * Shows the parent case's tier + refinement/retainer usage at a glance so
 * the doctor knows what their bundle covers before they request anything.
 *
 * Hidden when:
 *   - The case has no tier (legacy / orphan data).
 *   - The case is a child (refinement/retainer) — they have their own banner inside the wizard.
 *
 * Mounts inside WorkflowTab. Self-fetches /child-allowance for both kinds
 * so it stays decoupled from the parent component's data layer.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArrowsClockwise, Crown, Receipt } from "@phosphor-icons/react";

export default function TierAllowanceLedger({ caseData }) {
    const [ref, setRef] = useState(null);
    const [ret, setRet] = useState(null);

    const tierId = caseData?.pricing?.tier_id;
    const isChild = !!caseData?.parent_case_id;

    useEffect(() => {
        if (!caseData?.id || isChild || !tierId) return;
        let cancelled = false;
        Promise.all([
            api.get(`/doctor/cases/${caseData.id}/child-allowance`, { params: { kind: "refinement" } })
                .then((r) => !cancelled && setRef(r.data))
                .catch(() => !cancelled && setRef(null)),
            api.get(`/doctor/cases/${caseData.id}/child-allowance`, { params: { kind: "retainer" } })
                .then((r) => !cancelled && setRet(r.data))
                .catch(() => !cancelled && setRet(null)),
        ]);
        return () => { cancelled = true; };
    }, [caseData?.id, isChild, tierId]);

    if (!caseData || isChild || !tierId || (!ref && !ret)) return null;

    const tierName = ref?.tier_name || ret?.tier_name || caseData.pricing?.tier_name || "Tier";

    return (
        <div
            className="rounded-2xl border border-border bg-card overflow-hidden"
            data-testid="tier-allowance-ledger"
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/20">
                <div className="flex items-center gap-2">
                    <Receipt size={14} weight="duotone" className="text-primary" />
                    <div className="font-display font-bold text-sm">Tier allowance</div>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{tierName}</span>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
                <LedgerCell
                    icon={<ArrowsClockwise size={14} className="text-primary" />}
                    label="Refinements"
                    data={ref}
                    kind="refinement"
                />
                <LedgerCell
                    icon={<Crown size={14} className="text-violet-600" />}
                    label="Retainers"
                    data={ret}
                    kind="retainer"
                />
            </div>
        </div>
    );
}

function LedgerCell({ icon, label, data, kind }) {
    if (!data) {
        return (
            <div className="px-5 py-3 text-xs text-muted-foreground" data-testid={`ledger-cell-${kind}`}>
                {label} — not available
            </div>
        );
    }
    const included = data.included || 0;
    const used = data.used || 0;
    const remaining = data.remaining || 0;

    let tone, copy;
    if (included === 0) {
        tone = "text-muted-foreground";
        copy = `Not included in this tier`;
    } else if (remaining > 0) {
        tone = "text-emerald-700";
        copy = `${used} of ${included} used · ${remaining} remaining`;
    } else {
        tone = "text-amber-700";
        copy = `${used} of ${included} used · overage applies`;
    }

    return (
        <div className="px-5 py-3" data-testid={`ledger-cell-${kind}`}>
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-xs font-semibold text-foreground">{label}</span>
            </div>
            <div className={`text-xs mt-1 font-medium ${tone}`} data-testid={`ledger-${kind}-copy`}>
                {copy}
            </div>
            {kind === "refinement" && data.aligners_per_refinement > 0 && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                    Up to {data.aligners_per_refinement} aligners per refinement (soft cap)
                </div>
            )}
        </div>
    );
}
